import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { formatGBP } from "../lib/format";
import { CheckCircle2, Clock, AlertCircle, ArrowRight, Handshake } from "lucide-react";

const VERIFIED = {
  verified: { Icon: CheckCircle2, label: "Verified", color: "var(--rag-green-text)", bg: "var(--rag-green-bg)" },
  pending: { Icon: Clock, label: "Pending", color: "var(--rag-amber-text)", bg: "var(--rag-amber-bg)" },
  incomplete: { Icon: AlertCircle, label: "Incomplete", color: "var(--rag-red-text)", bg: "var(--rag-red-bg)" },
};

export default function Sponsors() {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.listSponsors().then((d) => { setSponsors(d); setLoading(false); }); }, []);

  return (
    <div className="space-y-8" data-testid="sponsors-page">
      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="signal-bar" />
          <span className="tracking-eyebrow text-muted-foreground">Sponsors &amp; supporters</span>
        </div>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-semibold leading-tight">
          Good sponsorship is not just funding. <span className="signal-text">It is fit.</span>
        </h1>
        <p className="text-sm text-muted-foreground max-w-2xl leading-relaxed">
          Match young talent with supporters whose aims, credentials, and values are clear.
          AI can suggest alignment — authorised humans approve the introduction.
        </p>
      </section>

      {loading && <Card className="p-8 text-sm text-muted-foreground">Loading sponsors…</Card>}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {sponsors.map((s) => <SponsorCard key={s.id} s={s} />)}
      </div>
    </div>
  );
}

function SponsorCard({ s }) {
  const v = VERIFIED[s.verified_status] || VERIFIED.incomplete;
  const c = s.constraints || {};
  const range = c.budget_range_gbp || [];
  return (
    <Link to={`/sponsors/${s.id}`} className="block group" data-testid={`sponsor-card-${s.id}`}>
      <Card className="p-5 border-border race-card flex flex-col gap-4 group-hover:shadow-md transition-shadow">
        <div className="flex items-start gap-3">
          <div className="h-12 w-12 rounded-md grid place-items-center text-white font-semibold font-mono-ui shrink-0" style={{ backgroundColor: s.logo_color }}>
            {s.logo_initials}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[16px] font-semibold truncate">{s.name}</div>
            <div className="text-[12px] text-muted-foreground">{s.type_label}</div>
          </div>
          <span className="inline-flex items-center gap-1 text-[10px] tracking-eyebrow font-mono-ui px-1.5 py-1 rounded-md" style={{ color: v.color, backgroundColor: v.bg }}>
            <v.Icon className="h-3 w-3" /> {v.label}
          </span>
        </div>

        <div>
          <div className="tracking-eyebrow text-muted-foreground mb-1">Aims</div>
          <div className="flex flex-wrap gap-1.5">
            {(s.aims || []).slice(0, 3).map((a) => (
              <span key={a} className="text-[11px] font-mono-ui px-1.5 py-0.5 rounded border border-border">{a}</span>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 text-xs">
          <div>
            <div className="tracking-eyebrow text-muted-foreground">Support offered</div>
            <div className="font-mono-ui mt-0.5 truncate">{(s.support_offered || []).slice(0, 3).join(" · ") || "—"}</div>
          </div>
          <div>
            <div className="tracking-eyebrow text-muted-foreground">Typical range</div>
            <div className="font-mono-ui mt-0.5">{range.length === 2 ? `${formatGBP(range[0])}–${formatGBP(range[1])}` : "—"}</div>
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-border pt-3">
          <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
            <Handshake className="h-3.5 w-3.5" /> View &amp; suggest match
          </span>
          <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
        </div>
      </Card>
    </Link>
  );
}
